import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronRight } from 'lucide-react';
import { create } from 'zustand';
import { cx } from './ui';

/** One entry of a right-click menu. `'sep'` draws a divider; falsy entries are skipped. */
export interface ContextItem {
  label: ReactNode;
  icon?: ReactNode;
  onClick?: () => void;
  /** Shortcut hint shown on the right, e.g. "Ctrl+D". */
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  /** Draws a checkmark (toggle items). */
  checked?: boolean;
  submenu?: ContextEntry[];
}
export type ContextEntry = ContextItem | 'sep' | false | null | undefined;

interface MenuState {
  open: { x: number; y: number; items: ContextEntry[] } | null;
}
const useMenu = create<MenuState>(() => ({ open: null }));

/** Normalises entries: drops falsy ones, collapses repeated / leading / trailing separators. */
function clean(items: ContextEntry[]): (ContextItem | 'sep')[] {
  const out: (ContextItem | 'sep')[] = [];
  for (const it of items) {
    if (!it) continue;
    if (it === 'sep' && (out.length === 0 || out[out.length - 1] === 'sep')) continue;
    out.push(it);
  }
  while (out[out.length - 1] === 'sep') out.pop();
  return out;
}

/**
 * Opens a context menu at the pointer. Use as an `onContextMenu` handler:
 * `onContextMenu={(e) => openContextMenu(e, [...])}`.
 * Items can be a function so they are built only when the menu opens.
 */
export function openContextMenu(
  e: { preventDefault(): void; stopPropagation(): void; clientX: number; clientY: number; target?: EventTarget | null },
  items: ContextEntry[] | (() => ContextEntry[]),
) {
  // Text fields keep the browser menu (copy, paste, spellcheck).
  const t = e.target as HTMLElement | null;
  if (t?.closest?.('input, textarea, select, [contenteditable="true"]')) return;
  e.preventDefault();
  e.stopPropagation();
  const list = clean(typeof items === 'function' ? items() : items);
  if (!list.length) return;
  useMenu.setState({ open: { x: e.clientX, y: e.clientY, items: list } });
}

export function closeContextMenu() {
  useMenu.setState({ open: null });
}

/**
 * Long-press opens context menus on touch screens. Android fires `contextmenu`
 * on long-press by itself; iOS does not, so after 550 ms without movement we
 * dispatch one at the touch point (unless the browser already did), and swallow
 * the click that follows when the finger lifts.
 */
function useLongPressContextMenu() {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let start: { x: number; y: number; target: Element } | null = null;
    let nativeFired = false;
    let synthetic = false;
    let swallowClickUntil = 0;
    const cancel = () => {
      if (timer) clearTimeout(timer);
      timer = null;
      start = null;
    };
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'touch' || !(e.target instanceof Element)) return;
      cancel();
      nativeFired = false;
      start = { x: e.clientX, y: e.clientY, target: e.target };
      timer = setTimeout(() => {
        const s = start;
        cancel();
        if (!s || nativeFired || s.target.closest('input, textarea, select, [contenteditable="true"]')) return;
        synthetic = true;
        s.target.dispatchEvent(new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: s.x, clientY: s.y }));
        synthetic = false;
        swallowClickUntil = Date.now() + 800;
      }, 550);
    };
    const move = (e: PointerEvent) => {
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) cancel();
    };
    const onNative = () => {
      if (synthetic) return;
      nativeFired = true;
      cancel();
    };
    const click = (e: MouseEvent) => {
      if (Date.now() < swallowClickUntil) {
        swallowClickUntil = 0;
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('pointerdown', down, true);
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', cancel, true);
    window.addEventListener('pointercancel', cancel, true);
    window.addEventListener('contextmenu', onNative, true);
    window.addEventListener('click', click, true);
    return () => {
      cancel();
      window.removeEventListener('pointerdown', down, true);
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', cancel, true);
      window.removeEventListener('pointercancel', cancel, true);
      window.removeEventListener('contextmenu', onNative, true);
      window.removeEventListener('click', click, true);
    };
  }, []);
}

/** Mount once near the app root. */
export function ContextMenuHost() {
  useLongPressContextMenu();
  const open = useMenu((s) => s.open);
  useEffect(() => {
    if (!open) return;
    const close = () => closeContextMenu();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (e.stopPropagation(), close());
    window.addEventListener('pointerdown', close);
    window.addEventListener('blur', close);
    window.addEventListener('resize', close);
    window.addEventListener('wheel', close, { passive: true });
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('blur', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('wheel', close);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  if (!open) return null;
  return createPortal(<MenuPanel x={open.x} y={open.y} items={open.items} root />, document.body);
}

/**
 * On the way to a submenu the pointer often crosses a neighbouring row;
 * switching at once would swap in that row's submenu (e.g. "Transition in" for
 * "Transition out", whose items look the same) under the pointer. So while the
 * pointer heads for the open submenu (inside the triangle from where it left
 * the row to the submenu's near edge) the switch waits, up to this long if it
 * comes to rest there. Anywhere else rows switch at once, so moving down the
 * menu never leaves the highlight stuck on a submenu row.
 */
const SUBMENU_SWITCH_MS = 250;

type Pt = { x: number; y: number };

/** Whether `p` lies in the triangle `a`, `b`, `c`. */
function inTriangle(p: Pt, a: Pt, b: Pt, c: Pt) {
  const side = (u: Pt, v: Pt) => (p.x - v.x) * (u.y - v.y) - (u.x - v.x) * (p.y - v.y);
  const d1 = side(a, b);
  const d2 = side(b, c);
  const d3 = side(c, a);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

function MenuPanel({
  x,
  y,
  items,
  root,
  alignRightOf,
  onEnter,
  panelRef,
}: {
  x: number;
  y: number;
  items: ContextEntry[];
  root?: boolean;
  alignRightOf?: number;
  /** The pointer reached this (sub)menu. */
  onEnter?: () => void;
  /** Receives this panel's element (the parent menu uses it to aim at its submenu). */
  panelRef?: React.RefObject<HTMLDivElement>;
}) {
  const ownRef = useRef<HTMLDivElement>(null);
  const ref = panelRef ?? ownRef;
  const subRef = useRef<HTMLDivElement>(null);
  const list = clean(items);
  const [pos, setPos] = useState({ left: x, top: y, ready: false });
  const [active, setActive] = useState(-1);
  const [sub, setSub] = useState<{ index: number; x: number; y: number; left: number } | null>(null);
  const switchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSwitch = useRef<(() => void) | null>(null);
  /** Where the pointer left the row whose submenu is open. */
  const exitPt = useRef<Pt | null>(null);
  const cancelSwitch = () => {
    if (switchTimer.current) clearTimeout(switchTimer.current);
    switchTimer.current = null;
    pendingSwitch.current = null;
  };
  /** Whether the pointer is on its way from the open submenu's row to the submenu. */
  const aimingAtSub = (p: Pt) => {
    const a = exitPt.current;
    const r = subRef.current?.getBoundingClientRect();
    if (!a || !r) return false;
    // The submenu's edge facing the pointer, padded a little vertically.
    const x = r.left >= a.x ? r.left : r.right;
    const dir = x >= a.x ? -1 : 1;
    const apex = { x: a.x + dir * 4, y: a.y };
    return inTriangle(p, apex, { x, y: r.top - 8 }, { x, y: r.bottom + 8 });
  };
  useEffect(() => cancelSwitch, []);

  // Keep the menu on screen: flip left/up when it would overflow.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let left = x;
    let top = y;
    if (left + w > window.innerWidth - 4) left = alignRightOf !== undefined ? alignRightOf - w : window.innerWidth - w - 4;
    if (top + h > window.innerHeight - 4) top = Math.max(4, window.innerHeight - h - 4);
    setPos({ left: Math.max(4, left), top, ready: true });
  }, [x, y, alignRightOf]);

  useEffect(() => {
    if (root) ref.current?.focus();
  }, [root]);

  const run = (it: ContextItem) => {
    if (it.disabled || it.submenu) return;
    closeContextMenu();
    it.onClick?.();
  };
  const openSub = (i: number, el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    exitPt.current = null;
    setSub({ index: i, x: r.right - 2, y: r.top - 5, left: r.left + 2 });
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const actionable = list.map((it, i) => (it !== 'sep' && !it.disabled ? i : -1)).filter((i) => i >= 0);
    if (!actionable.length) return;
    const pos = actionable.indexOf(active);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(actionable[(pos + 1) % actionable.length]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(actionable[(pos - 1 + actionable.length) % actionable.length]);
    } else if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowRight') {
      const it = list[active];
      if (!it || it === 'sep') return;
      e.preventDefault();
      if (it.submenu) {
        const el = ref.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`);
        if (el) openSub(active, el);
      } else if (e.key !== 'ArrowRight') run(it);
    } else if (e.key === 'ArrowLeft' && !root) {
      e.preventDefault();
      setSub(null);
    }
    e.stopPropagation();
  };

  return (
    <>
      <div
        ref={ref}
        tabIndex={-1}
        role="menu"
        className="fixed z-[80] max-h-[calc(100dvh-8px)] min-w-[200px] max-w-[min(320px,calc(100vw-8px))] overflow-y-auto rounded-xl border border-line bg-panel-2 py-1 text-sm shadow-2xl outline-none no-select"
        style={{ left: pos.left, top: pos.top, visibility: pos.ready ? 'visible' : 'hidden' }}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerEnter={onEnter}
        onPointerMove={(e) => {
          // Stopped heading for the submenu: switch to the row under the pointer now.
          const go = pendingSwitch.current;
          if (go && !aimingAtSub({ x: e.clientX, y: e.clientY })) {
            cancelSwitch();
            go();
          }
        }}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={onKeyDown}
      >
        {list.map((it, i) =>
          it === 'sep' ? (
            <div key={i} className="my-1 border-t border-line" />
          ) : (
            <button
              key={i}
              data-idx={i}
              role="menuitem"
              disabled={it.disabled}
              className={cx(
                'flex w-full items-center gap-2 px-3 py-1.5 text-left disabled:opacity-40 [@media(pointer:coarse)]:py-2.5',
                active === i && !it.disabled && 'bg-panel-3',
                it.danger ? 'text-danger' : 'text-fg',
              )}
              onPointerEnter={(e) => {
                cancelSwitch();
                const el = e.currentTarget;
                const go = () => {
                  switchTimer.current = null;
                  pendingSwitch.current = null;
                  setActive(i);
                  if (it.submenu && !it.disabled) openSub(i, el);
                  else setSub(null);
                };
                if (sub && sub.index !== i && aimingAtSub({ x: e.clientX, y: e.clientY })) {
                  pendingSwitch.current = go;
                  switchTimer.current = setTimeout(go, SUBMENU_SWITCH_MS);
                } else go();
              }}
              onPointerLeave={(e) => {
                if (sub?.index === i) exitPt.current = { x: e.clientX, y: e.clientY };
              }}
              onClick={(e) => {
                cancelSwitch();
                setActive(i);
                if (it.submenu) openSub(i, e.currentTarget);
                else run(it);
              }}
            >
              <span className="flex w-[14px] shrink-0 items-center justify-center text-muted">{it.checked ? <Check size={14} className="text-accent-2" /> : it.icon}</span>
              <span className="min-w-0 flex-1 truncate">{it.label}</span>
              {it.shortcut ? <span className="ml-4 shrink-0 font-mono text-[11px] text-faint [@media(pointer:coarse)]:hidden">{it.shortcut}</span> : null}
              {it.submenu ? <ChevronRight size={13} className="shrink-0 text-faint" /> : null}
            </button>
          ),
        )}
      </div>
      {sub && list[sub.index] && list[sub.index] !== 'sep' ? (
        <MenuPanel
          key={sub.index}
          x={sub.x}
          y={sub.y}
          alignRightOf={sub.left}
          panelRef={subRef}
          items={(list[sub.index] as ContextItem).submenu ?? []}
          onEnter={() => {
            cancelSwitch();
            setActive(sub.index);
          }}
        />
      ) : null}
    </>
  );
}

/** Platform-aware modifier label for shortcut hints. */
export const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';
